import { supabase } from './supabase';
import { BrokerAgreement, BrokerAgreementWithDetails, Apartment, BrokerProfile } from '../types';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';

/**
 * Generate the real estate brokerage services agreement text
 * Compliant with Real Estate Agents Law (1996) and March 2025 Ethics Regulations
 * 
 * @param apartment - The property details
 * @param renterName - The prospective renter's full name
 * @param renterIdNumber - The renter's 9-digit Israeli ID (Teudat Zehut)
 * @param language - 'en' or 'he' for English/Hebrew
 * @param brokerProfile - Broker's professional identification and commission details
 * @returns Formatted agreement text ready for signature
 */
export function generateAgreementText(
  apartment: Apartment, 
  renterName: string, 
  renterIdNumber?: string,
  language: string = 'en',
  brokerProfile?: BrokerProfile
): string {
  const address = apartment.address || apartment.neighborhood;
  const brokerName = apartment.landlord.name;
  const floor = apartment.floor !== undefined ? `${apartment.floor}` : 'לא צוין';
  const rooms = apartment.rooms;
  
  // Commission calculation with 2026 Israeli VAT rate (18%)
  const commissionPercentage = brokerProfile?.commission_percentage ?? 100;
  const commissionAmount = (apartment.price * commissionPercentage) / 100;
  const includesVAT = brokerProfile?.commission_includes_vat !== false;
  
  // Format date based on language
  const currentDate = language === 'he' 
    ? new Date().toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })
    : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  
  // Dual representation status (March 2025 Ethics Regulations requirement)
  const representsBoth = brokerProfile?.represents_both_parties === true;
  
  // Hebrew version
  if (language === 'he') {
    const vatText = includesVAT ? 'כולל מע"מ בשיעור 18%' : 'לא כולל מע"מ';
    const dualRepDisclosure = representsBoth
      ? 'המתווך מייצג הן את בעל הנכס והן את השוכר בעסקה זו. המתווך מתחייב לפעול בנייטרליות ובהגינות כלפי שני הצדדים, ולא להעדיף צד אחד על פני האחר.'
      : 'המתווך מייצג את בעל הנכס בלבד בעסקה זו. השוכר אינו לקוח של המתווך.';
    
    return `הזמנת שירותי תיווך במקרקעין

תאריך: ${currentDate}


זיהוי מתווך (חובה על פי חוק):

שם מתווך: ${brokerName}
תעודת זהות: ${brokerProfile?.broker_id_number || 'לא סופק'}
מספר רישיון תיווך: ${brokerProfile?.broker_license_number || 'לא סופק'}
מונפק על ידי: משרד האוצר הישראלי


גילוי ייצוג כפול (תקנות האתיקה 2024):

${dualRepDisclosure}


צדדים להסכם:

שוכר פוטנציאלי: ${renterName}
${renterIdNumber ? `מספר זהות: ${renterIdNumber}\n` : ''}
מתווך/משכיר: ${brokerName}
${brokerProfile?.broker_id_number ? `תעודת זהות: ${brokerProfile.broker_id_number}\n` : ''}${brokerProfile?.broker_license_number ? `מספר רישיון: ${brokerProfile.broker_license_number}\n` : ''}

פרטי הנכס:

נכס: ${apartment.title}
מיקום: ${address}
קומה: ${floor}
מספר חדרים: ${rooms}


תנאים והגבלות:

1. היקף השירות

השוכר הפוטנציאלי מאשר כי צפה בנכס דרך המתווך ומסכים לעבוד עם המתווך עבור נכס זה.


2. אי עקיפה

השוכר הפוטנציאלי מסכים לא לעקוף את המתווך על ידי יצירת קשר ישירות עם בעל הנכס או דרך מתווך אחר למטרת השכרה או חכירה של הנכס.


3. עמלת מתווך

במקרה שהשוכר הפוטנציאלי ימשיך להשכיר את הנכס, דמי העמלה יהיו:

סכום עמלה: ₪${commissionAmount.toLocaleString('he-IL')} (${commissionPercentage}% משכר דירה חודשי)
${vatText}


4. תוקף ההסכם

הסכם זה נשאר בתוקף למשך 90 יום מתאריך החתימה או עד שהנכס יושכר לצד אחר, המוקדם מביניהם.


5. מחויבות משפטית

זהו הסכם חתימה דיגיטלית מחייב משפטית תחת חוק החתימה האלקטרונית, התשס"א-2001.


הכרה:

בחתימה למטה, אני מכיר/ה שקראתי, הבנתי, ומסכים/ה לכל התנאים של הזמנת שירותי תיווך זו.


חתימה:

_______________________________________________

שם החותם/ת: ${renterName}

תאריך: ${currentDate}`;
  }
  
  // English version (default)
  const vatText = includesVAT ? 'including VAT at 18%' : 'excluding VAT';
  const floorText = apartment.floor !== undefined ? `${apartment.floor}` : 'Not specified';
  const dualRepDisclosure = representsBoth
    ? 'The Broker represents BOTH the property owner and the renter in this transaction. The Broker commits to act with neutrality and fairness toward both parties, and will not favor one party over the other.'
    : 'The Broker represents the property owner ONLY in this transaction. The Renter is not a client of the Broker.';
  
  return `ORDER FOR REAL ESTATE BROKERAGE SERVICES

Date: ${currentDate}


BROKER IDENTIFICATION (Mandatory per Law):

Broker Name: ${brokerName}
ID Number: ${brokerProfile?.broker_id_number || 'NOT PROVIDED'}
License Number: ${brokerProfile?.broker_license_number || 'NOT PROVIDED'}
Issued by: Israeli Ministry of Finance


DUAL REPRESENTATION DISCLOSURE (Ethics Regulations 2024):

${dualRepDisclosure}


PARTIES TO THIS AGREEMENT:

Prospective Renter: ${renterName}
${renterIdNumber ? `ID Number: ${renterIdNumber}\n` : ''}
Broker/Landlord: ${brokerName}
${brokerProfile?.broker_id_number ? `ID Number: ${brokerProfile.broker_id_number}\n` : ''}${brokerProfile?.broker_license_number ? `License Number: ${brokerProfile.broker_license_number}\n` : ''}

PROPERTY INFORMATION:

Property: ${apartment.title}
Location: ${address}
Floor: ${floorText}
Rooms: ${rooms}


TERMS AND CONDITIONS:

1. Scope of Services

The Prospective Renter acknowledges viewing the Property through the Broker and agrees to work with the Broker for this Property.


2. Non-Circumvention

The Prospective Renter agrees not to circumvent the Broker by contacting the property owner directly or through any other agent for the purpose of renting or leasing the Property.


3. Broker Commission

If the Prospective Renter proceeds to rent the Property, the commission fee shall be:

Commission Amount: ₪${commissionAmount.toLocaleString('en-US')} (${commissionPercentage}% of monthly rent)
${vatText}


4. Agreement Duration

This Agreement remains in effect for 90 days from the date of signature or until the Property is rented to another party, whichever comes first.


5. Legal Binding

This is a legally binding digital signature agreement under the Electronic Signature Law, 2001.


ACKNOWLEDGMENT:

By signing below, I acknowledge that I have read, understood, and agree to all terms of this Order for Real Estate Brokerage Services.


SIGNATURE:

_______________________________________________

Signer Name: ${renterName}

Date: ${currentDate}`;
}

/**
 * Generate SHA-256 hash of agreement text + signature for document integrity
 * This creates a cryptographic fingerprint that proves the document hasn't been tampered with
 * 
 * @param agreementText - The full agreement text content
 * @param signatureBlob - The signature image as a Blob
 * @returns 64-character hex string (SHA-256 hash)
 */
async function generateAgreementHash(
  agreementText: string,
  signatureBlob: Blob
): Promise<string | null> {
  try {
    // Convert agreement text to bytes
    const textBytes = new TextEncoder().encode(agreementText);
    
    // Convert signature blob to bytes
    const signatureArrayBuffer = await signatureBlob.arrayBuffer();
    const signatureBytes = new Uint8Array(signatureArrayBuffer);
    
    // Combine text and signature data into single byte array
    // Order matters: text first, then signature (must be consistent for verification)
    const combined = new Uint8Array(textBytes.length + signatureBytes.length);
    combined.set(textBytes, 0);
    combined.set(signatureBytes, textBytes.length);
    
    // Generate SHA-256 hash using Web Crypto API
    const hashBuffer = await crypto.subtle.digest('SHA-256', combined);
    
    // Convert hash to hex string (64 characters)
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return hashHex;
    
  } catch (error) {
    console.error('Error generating agreement hash:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'agreements', action: 'generate-hash' } 
    });
    return null;
  }
}

/**
 * Fetch client metadata (IP address, user agent, timestamp) from Edge Function
 * This provides server-authoritative data that cannot be spoofed by the client
 */
interface ClientMetadata {
  ipAddress: string;
  userAgent: string;
  timestamp: number;
  timestampISO: string;
}

async function fetchClientMetadata(): Promise<ClientMetadata | null> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      console.error('VITE_SUPABASE_URL is not defined');
      return null;
    }

    // Get auth token to pass to Edge Function
    const { data: { session } } = await supabase.auth.getSession();
    const authToken = session?.access_token;

    const response = await fetch(`${supabaseUrl}/functions/v1/get-client-metadata`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
      },
    });

    if (!response.ok) {
      console.error('Failed to fetch client metadata:', response.statusText);
      return null;
    }

    const metadata: ClientMetadata = await response.json();
    return metadata;

  } catch (error) {
    console.error('Error fetching client metadata:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'agreements', action: 'fetch-metadata' } 
    });
    return null;
  }
}

/**
 * Verify the integrity of a signed agreement by re-computing its hash
 * This detects if the agreement text or signature has been tampered with
 * 
 * @param agreement - The agreement record from database
 * @returns Object with isValid flag and details
 */
export async function verifyAgreementIntegrity(
  agreement: BrokerAgreement
): Promise<{ isValid: boolean; currentHash?: string; storedHash?: string; error?: string }> {
  try {
    // Check if agreement has a stored hash
    if (!agreement.document_hash) {
      return {
        isValid: false,
        error: 'No hash stored (agreement signed before integrity feature was implemented)'
      };
    }

    // Fetch the signature image from storage
    const signatureResponse = await fetch(agreement.signature_url);
    if (!signatureResponse.ok) {
      return {
        isValid: false,
        error: 'Failed to fetch signature image for verification'
      };
    }

    const signatureBlob = await signatureResponse.blob();

    // Re-generate hash using the same algorithm
    const currentHash = await generateAgreementHash(agreement.agreement_text, signatureBlob);
    
    if (!currentHash) {
      return {
        isValid: false,
        error: 'Failed to generate hash for comparison'
      };
    }

    // Compare hashes (case-insensitive, though they should always be lowercase)
    const isValid = currentHash.toLowerCase() === agreement.document_hash.toLowerCase();

    return {
      isValid,
      currentHash,
      storedHash: agreement.document_hash,
      ...(isValid ? {} : { error: 'Hash mismatch - document has been tampered with!' })
    };

  } catch (error) {
    console.error('Error verifying agreement integrity:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'agreements', action: 'verify-integrity' },
      extra: { agreementId: agreement.id }
    });
    return {
      isValid: false,
      error: error instanceof Error ? error.message : 'Unknown verification error'
    };
  }
}

/**
 * Check if a renter has already signed an agreement for an apartment
 */
export async function checkAgreementExists(
  renterId: string,
  apartmentId: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('signed_agreements')
      .select('id')
      .eq('renter_id', renterId)
      .eq('apartment_id', apartmentId)
      .maybeSingle();

    if (error) throw error;

    return !!data;
  } catch (error) {
    console.error('Error checking agreement existence:', error);
    Sentry.captureException(error, { tags: { feature: 'agreements', action: 'check-exists' } });
    return false;
  }
}

/**
 * Create a new broker agreement (sign and upload)
 * @param apartmentId - The apartment ID
 * @param agreementText - The full agreement text
 * @param signatureBlob - The signature image as a Blob
 * @param renterName - The renter's full name
 * @param renterIdNumber - The renter's ID number
 * @returns The created agreement or null on error
 */
export async function createAgreement(
  apartmentId: string,
  agreementText: string,
  signatureBlob: Blob,
  renterName?: string,
  renterIdNumber?: string
): Promise<BrokerAgreement | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error("You must be logged in to sign an agreement");
      return null;
    }

    // Fetch client metadata (IP, user agent, timestamp) - non-blocking
    // If this fails, we still allow the signing to proceed
    const metadata = await fetchClientMetadata();
    if (!metadata) {
      console.warn('Failed to capture client metadata, proceeding without audit trail');
      Sentry.captureMessage('Agreement signed without metadata capture', {
        level: 'warning',
        tags: { feature: 'agreements', action: 'metadata-missing' }
      });
    }

    // Generate cryptographic hash for document integrity (SHA-256)
    // This creates a tamper-evident seal of the agreement text + signature
    const documentHash = await generateAgreementHash(agreementText, signatureBlob);
    if (!documentHash) {
      console.warn('Failed to generate document hash, proceeding without integrity verification');
      Sentry.captureMessage('Agreement signed without document hash', {
        level: 'warning',
        tags: { feature: 'agreements', action: 'hash-missing' }
      });
    } else {
      // Log first 16 chars for debugging (never log full hash for security)
      console.log('Document hash generated:', documentHash.substring(0, 16) + '...');
    }

    // Get the apartment to find the broker/owner
    const { data: apartment, error: aptError } = await supabase
      .from('apartments')
      .select('owner_id')
      .eq('id', apartmentId)
      .single();

    if (aptError || !apartment?.owner_id) {
      toast.error("Could not find apartment owner");
      return null;
    }

    // 1. Upload signature to Supabase Storage
    const fileName = `${user.id}/${apartmentId}_${Date.now()}.png`;
    const { error: uploadError } = await supabase.storage
      .from('signatures')
      .upload(fileName, signatureBlob, {
        contentType: 'image/png',
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      toast.error("Failed to upload signature");
      return null;
    }

    // 2. Get the public URL for the signature
    const { data: urlData } = supabase.storage
      .from('signatures')
      .getPublicUrl(fileName);

    if (!urlData?.publicUrl) {
      toast.error("Failed to get signature URL");
      return null;
    }

    // Log for debugging

    // 3. Insert the signed agreement into the database
    const { data: agreement, error: insertError } = await supabase
      .from('signed_agreements')
      .insert({
        renter_id: user.id,
        apartment_id: apartmentId,
        broker_id: apartment.owner_id,
        agreement_text: agreementText,
        signature_url: urlData.publicUrl,
        renter_name: renterName,
        renter_id_number: renterIdNumber,
        // Audit trail fields
        ip_address: metadata?.ipAddress,
        user_agent: metadata?.userAgent,
        signed_at_utc: metadata?.timestampISO,
        // Document integrity field
        document_hash: documentHash
      })
      .select()
      .single();

    if (insertError) {
      console.error('Insert error:', insertError);
      
      // Clean up uploaded signature if insert fails
      await supabase.storage.from('signatures').remove([fileName]);
      
      if (insertError.code === '23505') {
        toast.error("You have already signed an agreement for this property");
      } else {
        toast.error("Failed to save agreement");
      }
      return null;
    }

    toast.success("Agreement signed successfully! 📝");
    return agreement;

  } catch (error: any) {
    console.error('Error creating agreement:', error);
    Sentry.captureException(error, { tags: { feature: 'agreements', action: 'create-agreement' } });
    toast.error('Failed to sign agreement');
    return null;
  }
}

/**
 * Fetch all agreements signed by a specific renter
 */
export async function fetchRenterAgreements(
  renterId?: string
): Promise<BrokerAgreementWithDetails[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const userId = renterId || user.id;

    const { data, error } = await supabase
      .from('signed_agreements')
      .select(`
        *,
        apartment:apartments(id, title, address, neighborhood, images)
      `)
      .eq('renter_id', userId)
      .order('signed_at', { ascending: false });

    if (error) throw error;

    return (data || []).map(item => ({
      ...item,
      apartment_title: item.apartment?.title,
      apartment_address: item.apartment?.address || item.apartment?.neighborhood
    }));

  } catch (error) {
    console.error('Error fetching renter agreements:', error);
    Sentry.captureException(error, { tags: { feature: 'agreements', action: 'fetch-renter-agreements' } });
    return [];
  }
}

/**
 * Fetch all agreements for apartments owned by a broker
 */
export async function fetchBrokerAgreements(
  brokerId?: string
): Promise<BrokerAgreementWithDetails[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const userId = brokerId || user.id;

    const { data, error } = await supabase
      .from('signed_agreements')
      .select(`
        *,
        apartment:apartments(id, title, address, neighborhood, images)
      `)
      .eq('broker_id', userId)
      .order('signed_at', { ascending: false });

    if (error) throw error;

    // Enhance with renter names if available
    return (data || []).map(item => ({
      ...item,
      apartment_title: item.apartment?.title,
      apartment_address: item.apartment?.address || item.apartment?.neighborhood,
      renter_name: 'Renter' // We don't have a profiles table yet
    }));

  } catch (error) {
    console.error('Error fetching broker agreements:', error);
    Sentry.captureException(error, { tags: { feature: 'agreements', action: 'fetch-broker-agreements' } });
    return [];
  }
}

/**
 * Fetch agreements for a specific apartment (for broker dashboard)
 */
export async function fetchApartmentAgreements(
  apartmentId: string
): Promise<BrokerAgreementWithDetails[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('signed_agreements')
      .select('*')
      .eq('apartment_id', apartmentId)
      .eq('broker_id', user.id) // Only show if current user is the broker
      .order('signed_at', { ascending: false });

    if (error) {
      console.error('Error fetching apartment agreements:', error);
      throw error;
    }


    return (data || []);

  } catch (error) {
    console.error('Error fetching apartment agreements:', error);
    Sentry.captureException(error, { tags: { feature: 'agreements', action: 'fetch-apartment-agreements' } });
    return [];
  }
}

/**
 * Fetch agreement for a specific apartment signed by the current renter
 */
export async function fetchAgreementForApartment(
  apartmentId: string
): Promise<BrokerAgreement | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('signed_agreements')
      .select('*')
      .eq('renter_id', user.id)
      .eq('apartment_id', apartmentId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching agreement for apartment:', error);
      Sentry.captureException(error, { tags: { feature: 'agreements', action: 'fetch-agreement-for-apartment' } });
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching agreement for apartment:', error);
    Sentry.captureException(error as Error, { tags: { feature: 'agreements', action: 'fetch-agreement-for-apartment' } });
    return null;
  }
}

/**
 * Fetch agreement counts for multiple apartments (efficient batch query)
 * Returns a map of apartment_id -> count of signed agreements
 */
export async function fetchAgreementCounts(
  apartmentIds: string[]
): Promise<Record<string, number>> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || apartmentIds.length === 0) return {};

    const { data, error } = await supabase
      .from('signed_agreements')
      .select('apartment_id')
      .in('apartment_id', apartmentIds)
      .eq('broker_id', user.id); // Only count agreements for current broker

    if (error) {
      console.error('Error fetching agreement counts:', error);
      Sentry.captureException(error, { tags: { feature: 'agreements', action: 'fetch-agreement-counts' } });
      return {};
    }

    // Count agreements per apartment
    const counts: Record<string, number> = {};
    apartmentIds.forEach(id => {
      counts[id] = 0; // Initialize all apartments with 0
    });
    
    (data || []).forEach(agreement => {
      const apartmentId = agreement.apartment_id;
      if (apartmentId && counts.hasOwnProperty(apartmentId)) {
        counts[apartmentId] = (counts[apartmentId] || 0) + 1;
      }
    });

    return counts;
  } catch (error) {
    console.error('Error fetching agreement counts:', error);
    Sentry.captureException(error, { tags: { feature: 'agreements', action: 'fetch-agreement-counts' } });
    return {};
  }
}

/**
 * Generate and download PDF for a signed agreement
 * Calls server-side Edge Function that handles Hebrew RTL and font embedding
 * 
 * @param agreementId - The agreement ID to generate PDF for
 * @returns The signed URL for downloading the PDF, or null on error
 */
export async function generateAgreementPDF(agreementId: string): Promise<string | null> {
  try {
    // Ensure we have a fresh session token (auto-refresh if needed)
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      toast.error('You must be logged in to generate PDFs');
      return null;
    }

    // Extra guard: ensure the token is actually valid
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      console.error('Supabase auth user check failed:', userError);
      toast.error('Your session expired. Please log in again.');
      return null;
    }

    // Show loading toast
    const loadingToast = toast.loading('Generating PDF...');

    try {
      // Call Edge Function to generate PDF.
      // NOTE: supabase.functions.invoke() AUTOMATICALLY includes Authorization header
      // from current session. Do NOT pass it manually or it may break.
      const { data: result, error } = await supabase.functions.invoke('generate-agreement-pdf', {
        body: { agreementId },
      });

      if (error) {
        // Try to extract the Edge Function response payload for clearer debugging (401/403/500).
        let details: string | undefined;
        try {
          // supabase-js error may include `context` (Response)
          const ctx: any = (error as any)?.context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            details =
              body?.error ??
              body?.message ??
              (typeof body === 'string' ? body : JSON.stringify(body));
          }
        } catch {
          // ignore parse errors
        }
        throw new Error(details || error.message || 'Failed to generate PDF');
      }

      if (!result.success || !result.pdfUrl) {
        throw new Error('Invalid response from PDF generator');
      }

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      // Show success message
      if (result.cached) {
        toast.success('PDF ready! (from cache)');
      } else {
        toast.success('PDF generated successfully! 📄');
      }

      return result.pdfUrl;

    } catch (error) {
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      throw error;
    }

  } catch (error: any) {
    console.error('Error generating PDF:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'agreements', action: 'generate-pdf' },
      extra: { agreementId }
    });
    toast.error(error.message || 'Failed to generate PDF');
    return null;
  }
}

/**
 * Download PDF for an agreement (generates if needed, then triggers download)
 * 
 * @param agreementId - The agreement ID
 * @param agreementTitle - Optional title for the downloaded file
 */
export async function downloadAgreementPDF(
  agreementId: string,
  agreementTitle?: string
): Promise<void> {
  try {
    // Generate or fetch PDF URL
    const pdfUrl = await generateAgreementPDF(agreementId);
    
    if (!pdfUrl) {
      return; // Error already handled in generateAgreementPDF
    }

    // Trigger download
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `agreement_${agreementTitle || agreementId}.pdf`;
    link.target = '_blank'; // Open in new tab as fallback
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  } catch (error: any) {
    console.error('Error downloading PDF:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'agreements', action: 'download-pdf' },
      extra: { agreementId }
    });
    toast.error('Failed to download PDF');
  }
}

/**
 * Get a specific agreement by ID
 */
export async function getAgreementById(agreementId: string): Promise<BrokerAgreement | null> {
  try {
    const { data, error } = await supabase
      .from('signed_agreements')
      .select('*')
      .eq('id', agreementId)
      .single();

    if (error) throw error;

    return data;

  } catch (error) {
    console.error('Error fetching agreement:', error);
    Sentry.captureException(error, { tags: { feature: 'agreements', action: 'get-by-id' } });
    return null;
  }
}

